import React, { useState, useEffect, useRef } from 'react';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Mail, Send, AlertCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseOrdersAPI, warehouseAPI, pausePolling, resumePolling } from '../../lib/api';
import { Link } from 'react-router-dom';

export const EmailVendorModal = ({ 
  open, 
  onOpenChange, 
  po,
  onEmailSent
}) => {
  const [sending, setSending] = useState(false);
  const [companyBranding, setCompanyBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [formData, setFormData] = useState({
    to_email: '', cc_emails: '', subject: '', message: ''
  });

  // Pause/resume polling when drawer opens/closes (ref-guarded for Strict Mode)
  const didPauseRef = useRef(false);
  useEffect(() => {
    if (open && !didPauseRef.current) {
      didPauseRef.current = true;
      pausePolling('EmailVendorModal-open');
    } else if (!open && didPauseRef.current) {
      didPauseRef.current = false;
      resumePolling();
    }
    return () => {
      if (didPauseRef.current) {
        didPauseRef.current = false;
        resumePolling();
      }
    };
  }, [open]);

  useEffect(() => {
    const loadBranding = async () => {
      if (!open) return;
      try {
        const response = await warehouseAPI.getCompanyBranding();
        setCompanyBranding(response.data);
      } catch (error) {
        console.error('Failed to load company branding:', error);
      } finally {
        setLoadingBranding(false);
      }
    };
    loadBranding();
  }, [open]);

  useEffect(() => {
    if (po && open) {
      setFormData({
        to_email: po.vendor_email || '',
        cc_emails: '',
        subject: `PO ${po.po_number} - ${companyBranding?.company_name || 'Order'}`,
        message: ''
      });
    }
  }, [po, open, companyBranding?.company_name]);

  useEffect(() => {
    if (po && companyBranding?.company_name) {
      setFormData(prev => ({ ...prev, subject: `PO ${po.po_number} - ${companyBranding.company_name}` }));
    }
  }, [po, companyBranding?.company_name]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!formData.to_email.trim()) { toast.error('Recipient email required'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.to_email.trim())) { toast.error('Invalid email format'); return; }
    if (sending) return;

    setSending(true);
    try {
      const ccEmails = formData.cc_emails.split(',').map(e => e.trim()).filter(e => e && emailRegex.test(e));
      const payload = {
        to_email: formData.to_email.trim(),
        cc_emails: ccEmails.length > 0 ? ccEmails : undefined,
        subject: formData.subject.trim() || undefined,
        message: formData.message.trim() || undefined
      };

      const submitRes = await purchaseOrdersAPI.emailVendor(po.id, payload);
      const { job_id } = submitRes.data;
      if (!job_id) throw new Error('No job ID returned');

      const POLL_INTERVAL = 1500;
      const MAX_POLL_TIME = 120000;
      const pollStart = Date.now();
      const poll = () => new Promise((resolve, reject) => {
        const tick = async () => {
          if (Date.now() - pollStart > MAX_POLL_TIME) return reject(new Error('Email send timed out. Please check again later.'));
          try {
            const res = await purchaseOrdersAPI.getEmailJobStatus(job_id);
            const { status, error } = res.data;
            if (status === 'sent') return resolve(res.data);
            if (status === 'failed') return reject(new Error(error || 'Email send failed'));
            setTimeout(tick, POLL_INTERVAL);
          } catch (err) {
            if (err.response?.status === 404) return reject(new Error(err.response?.data?.detail || 'Send failed due to server restart — please try again.'));
            return reject(err);
          }
        };
        tick();
      });

      await poll();
      toast.success(`Email sent to ${formData.to_email}`, { description: `PO ${po.po_number} delivered successfully` });
      onEmailSent?.();
      onOpenChange(false);
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Failed to send email';
      toast.error(`Email failed: ${message}`);
    } finally {
      setSending(false);
    }
  };

  const companyEmailMissing = !loadingBranding && !companyBranding?.company_email;
  const isDirty = formData.message !== '' || formData.cc_emails !== '';

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email PO to Vendor
        </span>
      }
      description={`Send PO ${po?.po_number || ''} to the vendor with PDF attachment.`}
      size="md"
      isDirty={isDirty}
      data-testid="email-vendor-drawer"
      footer={
        !loadingBranding && !companyEmailMissing ? (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="email-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending || !formData.to_email.trim()} data-testid="send-email-button">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="email-close-btn">
            Close
          </Button>
        )
      }
    >
      {loadingBranding ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : companyEmailMissing ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <p className="font-medium">Company email required</p>
            <p className="text-sm mt-1">
              Set a company email in Company Settings → Branding to email POs to vendors.
            </p>
            <Link to="/company-settings"
              className="inline-flex items-center gap-1 text-sm mt-2 underline hover:no-underline"
              onClick={() => onOpenChange(false)}>
              <Settings className="w-3 h-3" />Go to Company Settings
            </Link>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-5">
          {/* To Email */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">To *</Label>
            <Input type="email" value={formData.to_email}
              onChange={(e) => handleChange('to_email', e.target.value)}
              placeholder="vendor@example.com" className="h-12"
              data-testid="email-to-input" />
            <p className="text-xs text-muted-foreground">Vendor: {po?.vendor_name}</p>
          </div>

          {/* CC Emails */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">CC (optional)</Label>
            <Input value={formData.cc_emails}
              onChange={(e) => handleChange('cc_emails', e.target.value)}
              placeholder="email1@example.com, email2@example.com" className="h-12"
              data-testid="email-cc-input" />
            <p className="text-xs text-muted-foreground">Separate multiple emails with commas</p>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Subject</Label>
            <Input value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder={`PO ${po?.po_number}`} className="h-12"
              data-testid="email-subject-input" />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Message (optional)</Label>
            <Textarea value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Add a note to the vendor..." rows={3} className="resize-none"
              data-testid="email-message-input" />
          </div>

          {/* Reply-To Info */}
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-3">
            <p><strong>Reply-To:</strong> {companyBranding?.company_name} &lt;{companyBranding?.company_email}&gt;</p>
            <p className="mt-1">Vendor replies will go directly to your company email.</p>
          </div>
        </div>
      )}
    </AppDrawer>
  );
};

export default EmailVendorModal;
